/**
 * Add comment feature module.
 *
 * Purpose: End-to-end add comment orchestration.
 * Orchestrates adapters and core to implement the add comment flow.
 *
 * Key responsibilities:
 * - Get Velt comment element
 * - Extract selection context from editor
 * - Create comment via Velt SDK
 * - Apply mark to editor document
 * - Update state with annotation data
 *
 * Dependencies:
 * - adapters/velt.ts (for Velt SDK calls)
 * - adapters/host/doc.ts (for selection extraction, editor setup, and selection reset)
 * - adapters/host/marks.ts (for mark application)
 * - adapters/host/storage.ts (for mark application configuration)
 * - core/state.ts (for state updates)
 * - types/common.ts (for CommentAnnotationContext)
 *
 * IMPORTANT: This module MUST NOT import editor or Velt types directly.
 * All SDK access goes through adapters.
 */
import { __awaiter } from "tslib";
import { applyAnnotationMarkByText } from '@/adapters/host/marks';
import { ensureEditorSetup, getCurrentSelectionContext, resetEditorSelection } from '@/adapters/host/doc';
import { shouldApplyMark } from '@/adapters/host/storage';
import { addVeltComment, getCommentElement } from '@/adapters/velt';
import { Constants } from '@/constants/common';
import { updateAnnotations } from '@/core/state';
import { Console } from '@/utils/console';
/**
 * Adds a comment to the currently selected text in the editor.
 *
 * This is the main entry point for adding comments. It handles:
 * - Extracting selection context from the editor
 * - Creating the comment via Velt SDK
 * - Applying marks to the editor if configured
 * - Updating state with annotation data
 *
 * @param request - Object containing editorId (optional), editor, and context (optional)
 * @returns Promise that resolves when the comment is added (or fails silently)
 *
 * @example
 * ```typescript
 * // Simple usage
 * await addComment({ editor });
 *
 * // With editor ID
 * await addComment({ editorId: 'my-editor', editor });
 *
 * // With custom context
 * await addComment({
 *   editorId: 'my-editor',
 *   editor,
 *   context: {
 *     userId: 'user123',
 *     metadata: { source: 'web-app' }
 *   }
 * });
 * ```
 *
 * @remarks
 * - Requires text to be selected in the editor
 * - Requires Velt SDK to be available (window.Velt)
 * - Marks are only applied if persistVeltMarks is FALSE in extension config (matching legacy behavior)
 * - Fails silently if Velt SDK is unavailable or selection is invalid
 */
export function addComment(_a) {
    return __awaiter(this, arguments, void 0, function* ({ editorId, editor, context: clientContext, }) {
        const logPrefix = '[addComment]';
        // Validate editor parameter
        if (!editor) {
            Console.catch(`${logPrefix} ERROR: No editor provided`);
            return;
        }
        try {
            // Step 1: Get Velt comment element
            const commentElement = getCommentElement();
            if (!commentElement) {
                Console.warn(`${logPrefix} Velt SDK not available, cannot add comment`);
                return;
            }
            // Step 2: Extract selection context from editor
            // Type assertion needed because editor type is generic (unknown) to avoid importing editor types
            const selectionContext = getCurrentSelectionContext(editor);
            if (!selectionContext) {
                Console.warn(`${logPrefix} No valid selection found, exiting`);
                return;
            }
            // Step 3: Ensure editor is set up (registered and document listeners configured)
            const { editorId: finalEditorId } = ensureEditorSetup(editorId, editor);
            // Step 4: Build context object for Velt SDK
            // Only include editorId in textEditorConfig if user explicitly provided it
            // (either directly or via editor storage/DOM). DEFAULT_EDITOR_ID means it was auto-generated,
            // so we omit it for backward compatibility with old data that has no editorId.
            const userProvidedEditorId = editorId !== undefined || finalEditorId !== Constants.DEFAULT_EDITOR_ID;
            const context = Object.assign(Object.assign({}, (clientContext && typeof clientContext === 'object' ? clientContext : {})), { textEditorConfig: Object.assign(Object.assign({ text: selectionContext.text, occurrence: selectionContext.occurrence }, (userProvidedEditorId && { editorId: finalEditorId })), { targetTextNodeId: selectionContext.targetTextNodeId }) });
            // Step 5: Build location object if locationId is available
            let location;
            if (selectionContext.locationId) {
                location = { id: selectionContext.locationId };
            }
            // 5.1 Short delay to prevent comment dialog from closing immediately
            // This improves user experience when interacting with comments
            yield new Promise((resolve) => setTimeout(resolve, 200));
            // Step 6: Add comment via Velt SDK (matching legacy behavior - mark applied in callback)
            const veltResult = yield addVeltComment({ context, location });
            if (!veltResult || !veltResult.annotation) {
                Console.warn(`${logPrefix} Failed to create comment via Velt SDK`);
                return;
            }
            const annotation = veltResult.annotation;
            // Step 7: Check if marks should be applied (matching legacy behavior)
            // This means: if persistVeltMarks is FALSE, apply the mark
            const shouldApplyMarkValue = shouldApplyMark(editor, Constants.STORAGE_KEY);
            // Step 8: Apply mark to editor if needed (matching legacy behavior)
            // Legacy applies mark in the .then() callback, but we've already awaited, so apply now
            // Use text + occurrence approach for correct mark positioning across all document structures
            if (shouldApplyMarkValue && annotation.annotationId) {
                applyAnnotationMarkByText(editor, selectionContext.text, selectionContext.occurrence, annotation.annotationId, annotation.multiThreadAnnotationId, selectionContext.targetTextNodeId);
            }
            // Step 9: Update state with new annotation
            if (annotation.annotationId) {
                updateAnnotations(finalEditorId, [annotation]);
            }
            else {
                Console.warn(`${logPrefix} No annotationId, skipping state update`);
            }
            // Step 10: Reset editor selection (matching legacy behavior)
            resetEditorSelection(editor);
        }
        catch (error) {
            Console.catch(`${logPrefix} Error adding comment:`, error);
        }
    });
}
